import { clearOtherAdvancedStyles } from "@blocks/utils";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React, { useMemo } from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import LayoutOptionsRenderer from "../../../components/common/LayoutOptionsRenderer";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	CategoryListLayout1Style1,
	CategoryListLayout2Style1,
	CategoryListLayout2Style2,
	CategoryListLayout2Style3,
	CategoryListLayout3Style1,
	CategoryListLayout3Style2,
	HeadingLayout1Style1,
	HeadingLayout2Style1,
	HeadingLayout3Style1,
	HeadingLayout4Style1,
	HeadingLayout5Style1,
} from "../../../components/icons/Icons";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	IconPicker,
	Input,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			label,
			layout,
			layout1AdvancedStyle,
			layout2AdvancedStyle,
			layout3AdvancedStyle,
			postBoxStyle,
			categoryCount,
			gap,
			enableHeading,
			headingLayout,
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingColor,
			headingHoverColor,
			headingBorderColor,
			headingBorderColorBefore,
			headingBorderWidth,
			headingBorderWidthBefore,
			headingBorderBottomInset,
			headingBackground,
			headingTypography,
			dotColor,
			lineColor,
			lineAccent,

			cardBorder,
			cardBoxShadow,

			categoryTitleMarkup,
			categoryTitleTypography,
			categoryTitleColor,
			categoryTitleHoverColor,

			countColor,
			countHoverColor,
			countBackground,
			countHoverBackground,
			countWidth,

			blockPadding,
			blockMargin,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			listIcon,
			categoryArrow,
			spacing,
			height,
		},
		setAttributes,
	} = props;

	const categoryLayoutsData = useMemo(
		() => [
			{
				id: "layout-1",
				title: __("Layout 1", "magazine-blocks"),
				attrKey: "layout1AdvancedStyle",
				value: layout1AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "layout-1-style-1",
						icon: CategoryListLayout1Style1,
					},
					{
						label: __("Style 2", "magazine-blocks"),
						value: "layout-1-style-2",
						icon: CategoryListLayout1Style1,
					},
					{
						label: __("Style 3", "magazine-blocks"),
						value: "layout-1-style-3",
						icon: CategoryListLayout1Style1,
					},
				],
			},
			{
				id: "layout-2",
				title: __("Layout 2", "magazine-blocks"),
				attrKey: "layout2AdvancedStyle",
				value: layout2AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "layout-2-style-1",
						icon: CategoryListLayout2Style1,
					},
					{
						label: __("Style 2", "magazine-blocks"),
						value: "layout-2-style-2",
						icon: CategoryListLayout2Style2,
					},
					{
						label: __("Style 3", "magazine-blocks"),
						value: "layout-2-style-3",
						icon: CategoryListLayout2Style3,
					},
				],
			},
			{
				id: "layout-3",
				title: __("Layout 3", "magazine-blocks"),
				attrKey: "layout3AdvancedStyle",
				value: layout3AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "layout-3-style-1",
						icon: CategoryListLayout3Style1,
					},
					{
						label: __("Style 2", "magazine-blocks"),
						value: "layout-3-style-2",
						icon: CategoryListLayout3Style2,
					},
				],
			},
		],
		[layout1AdvancedStyle, layout2AdvancedStyle, layout3AdvancedStyle]
	);

	const headingLayoutData = useMemo(
		() => [
			{
				id: "heading-layout-1",
				title: __("Layout 1", "magazine-blocks"),
				attrKey: "headingLayout1AdvancedStyle",
				value: headingLayout1AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-1-style-1",
						icon: HeadingLayout1Style1,
					},
				],
			},
			{
				id: "heading-layout-2",
				title: __("Layout 2", "magazine-blocks"),
				attrKey: "headingLayout2AdvancedStyle",
				value: headingLayout2AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-2-style-1",
						icon: HeadingLayout2Style1,
					},
				],
			},
			{
				id: "heading-layout-3",
				title: __("Layout 3", "magazine-blocks"),
				attrKey: "headingLayout3AdvancedStyle",
				value: headingLayout3AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-3-style-1",
						icon: HeadingLayout3Style1,
					},
				],
			},
			{
				id: "heading-layout-4",
				title: __("Layout 4", "magazine-blocks"),
				attrKey: "headingLayout4AdvancedStyle",
				value: headingLayout4AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-4-style-1",
						icon: HeadingLayout4Style1,
					},
				],
			},
			{
				id: "heading-layout-5",
				title: __("Layout 5", "magazine-blocks"),
				attrKey: "headingLayout5AdvancedStyle",
				value: headingLayout5AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-5-style-1",
						icon: HeadingLayout5Style1,
					},
				],
			},
		],
		[
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
		]
	);

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")} initialOpen>
					<LayoutOptionsRenderer
						layoutsData={categoryLayoutsData}
						selectedValues={Object.fromEntries(
							categoryLayoutsData.map((ld) => [
								ld.attrKey,
								ld.value,
							])
						)}
						onChange={(attrKey, value, layoutId) =>
							setAttributes({
								...clearOtherAdvancedStyles(
									categoryLayoutsData,
									attrKey
								),
								[attrKey]: value,
								layout: layoutId,
							})
						}
						currentLayout={layout}
					/>
					<Slider
						onChange={(val: any) => setAttributes({ gap: val })}
						label={__("Gap", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={5}
						max={100}
						value={gap}
						resetAttributeKey="gap"
					/>
					{"layout-3" === layout &&
						"layout-3-style-1" === layout3AdvancedStyle && (
							<>
								<Toggle
									checked={postBoxStyle}
									onChange={() =>
										setAttributes({
											postBoxStyle: !postBoxStyle,
										})
									}
									label={__("Separator", "magazine-blocks")}
								/>
								<IconPicker
									hasToggle={true}
									value={listIcon || {}}
									onChange={(val) =>
										setAttributes({ listIcon: val })
									}
								/>
							</>
						)}
				</Panel>
				<Panel title={__("Post Options", "magazine-blocks")}>
					<Slider
						label={__("Category Count", "magazine-blocks")}
						value={categoryCount}
						step={1}
						min={1}
						max={15}
						onChange={(val) =>
							setAttributes({ categoryCount: val })
						}
						resetAttributeKey="categoryCount"
					/>
				</Panel>
				<Panel title={__("Heading", "magazine-blocks")}>
					<Toggle
						checked={enableHeading}
						onChange={() =>
							setAttributes({ enableHeading: !enableHeading })
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableHeading && (
						<>
							<Input
								onChange={(val) =>
									setAttributes({ label: val })
								}
								label={__("Label", "magazine-blocks")}
								value={label || ""}
							/>
							<LayoutOptionsRenderer
								columns={2}
								layoutsData={headingLayoutData}
								selectedValues={Object.fromEntries(
									headingLayoutData.map((ld) => [
										ld.attrKey,
										ld.value,
									])
								)}
								onChange={(attrKey, value, layoutId) =>
									setAttributes({
										...clearOtherAdvancedStyles(
											headingLayoutData,
											attrKey
										),
										[attrKey]: value,
										headingLayout: layoutId,
									})
								}
								currentLayout={layout}
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Post Title", "magazine-blocks")}>
					<Select
						options={[
							{
								label: __("H1", "magazine-blocks"),
								value: "h1",
							},
							{
								label: __("H2", "magazine-blocks"),
								value: "h2",
							},
							{
								label: __("H3", "magazine-blocks"),
								value: "h3",
							},
							{
								label: __("H4", "magazine-blocks"),
								value: "h4",
							},
							{
								label: __("H5", "magazine-blocks"),
								value: "h5",
							},
							{
								label: __("H6", "magazine-blocks"),
								value: "h6",
							},
						]}
						onChange={(val) =>
							setAttributes({ categoryTitleMarkup: val })
						}
						value={categoryTitleMarkup}
						label={__("HTML Markup", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Count", "magazine-blocks")}>
					<Slider
						// @ts-ignore
						onChange={(val) => setAttributes({ countWidth: val })}
						label={__("Width", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={300}
						value={countWidth}
						resetAttributeKey="countWidth"
					/>
				</Panel>
				{("layout-2-style-2" === layout2AdvancedStyle ||
					"layout-2-style-3" === layout2AdvancedStyle) && (
					<Panel title={__("Icon", "magazine-blocks")}>
						<IconPicker
							hasToggle={true}
							value={categoryArrow || {}}
							onChange={(val) => {
								setAttributes({ categoryArrow: val });
							}}
						/>
					</Panel>
				)}
				{("layout-2-style-2" === layout2AdvancedStyle ||
					"layout-2-style-3" === layout2AdvancedStyle ||
					"layout-3-style-2" === layout3AdvancedStyle) && (
					<Panel title={__("Border", "magazine-blocks")}>
						<Border
							value={cardBorder}
							onChange={(v) => {
								setAttributes({
									cardBorder: v,
								});
							}}
							resetAttributeKey="cardBorder"
						/>
						<BoxShadow
							resetAttributeKey="cardBoxShadow"
							value={cardBoxShadow}
							onChange={(val) =>
								setAttributes({
									cardBoxShadow: val,
								})
							}
						/>
					</Panel>
				)}
			</GeneralInspectorControls>
			<StyleInspectorControls>
				{"layout-1" === layout &&
					"layout-1-style-3" === layout1AdvancedStyle && (
						<Panel
							title={__("General", "magazine-blocks")}
							initialOpen
						>
							<Slider
								onChange={(val: any) =>
									setAttributes({ spacing: val })
								}
								label={__("Spacing", "magazine-blocks")}
								units={["px", "em", "%"]}
								responsive={true}
								min={5}
								max={500}
								value={spacing}
								resetAttributeKey="spacing"
							/>
							<Slider
								onChange={(val: any) =>
									setAttributes({ height: val })
								}
								label={__("Height", "magazine-blocks")}
								units={["px", "em", "%"]}
								responsive={true}
								min={5}
								max={800}
								value={height}
								resetAttributeKey="height"
							/>
						</Panel>
					)}
				{enableHeading && (
					<Panel title={__("Heading", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: headingColor,
												id: "headingColor",
											},
											{
												color: headingHoverColor,
												id: "headingHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={headingColor}
											resetKey="headingColor"
											onReset={(v) => {
												setAttributes({
													headingColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={headingColor}
											onChange={(v) =>
												setAttributes({
													headingColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={headingHoverColor}
											resetKey="headingHoverColor"
											onReset={(v) => {
												setAttributes({
													headingHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={headingHoverColor}
											onChange={(v) =>
												setAttributes({
													headingHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						{"heading-layout-2" === headingLayout && (
							<PopoverDrawer
								label={__(
									"Background Color",
									"magazine-blocks"
								)}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: headingBackground,
													id: "headingBackground",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={headingBackground}
												resetKey="headingBackground"
												onReset={(v) => {
													setAttributes({
														headingBackground: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={headingBackground}
												onChange={(v) =>
													setAttributes({
														headingBackground: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						)}
						<Typography
							value={headingTypography}
							onChange={(val) =>
								setAttributes({ headingTypography: val })
							}
							resetAttributeKey="headingTypography"
						/>
						<PopoverDrawer
							label={__("Border", "magazine-blocks")}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Before", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={headingBorderColor}
											resetKey="headingBorderColor"
											onReset={(v) => {
												setAttributes({
													headingBorderColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={headingBorderColor}
											onChange={(v) =>
												setAttributes({
													headingBorderColor: v,
												})
											}
										/>
										<Slider
											onChange={(val: any) =>
												setAttributes({
													headingBorderWidth: val,
												})
											}
											label={__(
												"Border Width",
												"magazine-blocks"
											)}
											units={["px", "em", "%"]}
											min={1}
											max={10}
											value={headingBorderWidth}
											resetAttributeKey="headingBorderWidth"
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={headingBorderColorBefore}
											resetKey="headingBorderColorBefore"
											onReset={(v) => {
												setAttributes({
													headingBorderColorBefore: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={headingBorderColorBefore}
											onChange={(v) =>
												setAttributes({
													headingBorderColorBefore: v,
												})
											}
										/>
										<Slider
											onChange={(val: any) =>
												setAttributes({
													headingBorderWidthBefore:
														val,
												})
											}
											label={__(
												"Border Width",
												"magazine-blocks"
											)}
											units={["px", "em", "%"]}
											min={1}
											max={10}
											value={headingBorderWidthBefore}
											resetAttributeKey="headingBorderWidthBefore"
										/>
										{headingLayout3AdvancedStyle && (
											<Slider
												onChange={(val: any) =>
													setAttributes({
														headingBorderBottomInset:
															val,
													})
												}
												label={__(
													"Border Bottom Inset",
													"magazine-blocks"
												)}
												units={["px", "em", "%"]}
												min={-100}
												max={100}
												value={headingBorderBottomInset}
												resetAttributeKey="headingBorderBottomInset"
											/>
										)}
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						{("heading-layout-1" === headingLayout ||
							"heading-layout-3" === headingLayout ||
							"heading-layout-7" === headingLayout ||
							"heading-layout-8" === headingLayout) && (
							<>
								<PopoverDrawer
									label={__("Line Accent", "magazine-blocks")}
									trigger={(props) => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: lineAccent,
														id: "lineAccent",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									)}
									closeOnFocusOutside
								>
									<Tabs position="relative">
										<TabList>
											<Tab>
												{__(
													"Normal",
													"magazine-blocks"
												)}
											</Tab>
										</TabList>
										<TabPanels>
											<TabPanel>
												<Reset
													saved={lineAccent}
													resetKey="lineAccent"
													onReset={(v) => {
														setAttributes({
															lineAccent: v,
														});
													}}
													buttonProps={{
														position: "absolute",
														top: "12px",
														right: "10px",
													}}
												/>
												<ColorPicker
													value={lineAccent}
													onChange={(v) =>
														setAttributes({
															lineAccent: v,
														})
													}
												/>
											</TabPanel>
										</TabPanels>
									</Tabs>
								</PopoverDrawer>
							</>
						)}

						{("heading-layout-2" === headingLayout ||
							"heading-layout-3" === headingLayout ||
							"heading-layout-4" === headingLayout ||
							"heading-layout-5" === headingLayout ||
							"heading-layout-5" === headingLayout ||
							"heading-layout-6" === headingLayout ||
							"heading-layout-7" === headingLayout ||
							"heading-layout-9" === headingLayout) && (
							<>
								<PopoverDrawer
									label={__("Line Color", "magazine-blocks")}
									trigger={(props) => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: lineColor,
														id: "lineColor",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									)}
									closeOnFocusOutside
								>
									<Tabs position="relative">
										<TabList>
											<Tab>
												{__(
													"Normal",
													"magazine-blocks"
												)}
											</Tab>
										</TabList>
										<TabPanels>
											<TabPanel>
												<Reset
													saved={lineColor}
													resetKey="lineColor"
													onReset={(v) => {
														setAttributes({
															lineColor: v,
														});
													}}
													buttonProps={{
														position: "absolute",
														top: "12px",
														right: "10px",
													}}
												/>
												<ColorPicker
													value={lineColor}
													onChange={(v) =>
														setAttributes({
															lineColor: v,
														})
													}
												/>
											</TabPanel>
										</TabPanels>
									</Tabs>
								</PopoverDrawer>
							</>
						)}

						{("heading-layout-4" === headingLayout ||
							"heading-layout-5" === headingLayout) && (
							<PopoverDrawer
								label={__("Dot Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: dotColor,
													id: "dotColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={dotColor}
												resetKey="dotColor"
												onReset={(v) => {
													setAttributes({
														dotColor: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={dotColor}
												onChange={(v) =>
													setAttributes({
														dotColor: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						)}
					</Panel>
				)}
				<Panel title={__("Post Title", "magazine-blocks")}>
					<Typography
						value={categoryTitleTypography}
						onChange={(val) =>
							setAttributes({ categoryTitleTypography: val })
						}
						resetAttributeKey="categoryTitleTypography"
					/>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: categoryTitleColor,
											id: "categoryTitleColor",
										},
										{
											color: categoryTitleHoverColor,
											id: "categoryTitleHoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={categoryTitleColor}
										resetKey="categoryTitleColor"
										onReset={(v) => {
											setAttributes({
												categoryTitleColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={categoryTitleColor}
										onChange={(v) =>
											setAttributes({
												categoryTitleColor: v,
											})
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={categoryTitleHoverColor}
										resetKey="categoryTitleHoverColor"
										onReset={(v) => {
											setAttributes({
												categoryTitleHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={categoryTitleHoverColor}
										onChange={(v) =>
											setAttributes({
												categoryTitleHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Count", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: countColor, id: "countColor" },
										{
											color: countHoverColor,
											id: "countHoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={countColor}
										resetKey="countColor"
										onReset={(v) => {
											setAttributes({ countColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={countColor}
										onChange={(v) =>
											setAttributes({ countColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={countHoverColor}
										resetKey="countHoverColor"
										onReset={(v) => {
											setAttributes({
												countHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={countHoverColor}
										onChange={(v) =>
											setAttributes({
												countHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer
						label={__("Background Color", "magazine-blocks")}
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={countBackground}
										resetKey="countBackground"
										onReset={(v) => {
											setAttributes({
												countBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={countBackground}
										onChange={(v) =>
											setAttributes({
												countBackground: v,
											})
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={countHoverBackground}
										resetKey="countHoverBackground"
										onReset={(v) => {
											setAttributes({
												countHoverBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={countHoverBackground}
										onChange={(v) =>
											setAttributes({
												countHoverBackground: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Slider
						// @ts-ignore
						onChange={(val) => setAttributes({ countWidth: val })}
						label={__("Width", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={300}
						value={countWidth}
						resetAttributeKey="countWidth"
					/>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
